import { useSessionStorage } from '@cheqroom/hooks';
import { UseBaseMutationResult } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useDoClearSession } from '../../api/clear-session.api';
import {
	OrganisationWithWorkspaces,
	resolveDiscoveryWorkspaces,
	useDoDiscovery,
	Workspace,
} from '../../api/discovery.api';
import { useDoTokenExchange } from '../../api/exchange-code.api';
import { HttpStatusError } from '../../api/http-status-error';
import { Step } from '../../App';
import { generateStrategyUrlForWorkspace } from '../SelectAuthenticationStrategyStep/utils';

type GroupedWorkspace = Omit<Workspace, 'strategy'> & { url: string };

const generateLoginStrategiesForWorkspaces = (workspaces: Workspace[]): GroupedWorkspace[] => {
	const uniqueWorkspacesWithCorrectRedirectUrl = workspaces.reduce((workspaces, workspace) => {
		const getStrategyLocation = generateStrategyUrlForWorkspace(workspace.id);

		const workspaceAlreadyExists = workspaces.has(workspace.id);
		const url = workspaceAlreadyExists ? `/${workspace.id}/login` : getStrategyLocation(workspace.strategy);

		return workspaces.set(workspace.id, { ...workspace, url });
	}, new Map<string, GroupedWorkspace>());

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	return [...uniqueWorkspacesWithCorrectRedirectUrl].map(([_, value]) => value);
};

export interface SelectWorkspacesData {
	isLoading: boolean;
	workspaces: GroupedWorkspace[];
	email?: string;
	selectWorkspace: (workspaceUrl: string) => void;
}

interface SelectWorkspaceStepState {
	state: {
		error?: string;
	};
}

export const useLoadWorkspaces = (organisationId?: string): SelectWorkspacesData => {
	const [isLoading, setIsLoading] = useState(true);
	const { t } = useTranslation();

	const [params] = useSearchParams();
	const token = params.get('token');

	const navigateTo = useNavigate();

	const { state: locationState } = useLocation() as SelectWorkspaceStepState;
	const [organisationIdFromSession, , clearOrganisationInSession] = useSessionStorage<string | undefined>(
		'organisation'
	);

	// Scoped GET /identity?organisationId=X calls fall back to the unscoped call once, on a 404,
	// to re-derive the user's current valid org set instead of funneling them into "join a new org".
	const [activeOrganisationId, setActiveOrganisationId] = useState(organisationId ?? organisationIdFromSession);
	const hasRetriedUnscopedRef = useRef(false);

	const clearSessionOnTokenExchangeFailure = useDoClearSession({
		onSettled: () => {
			navigateTo(Step.ENTER_EMAIL, { replace: true, state: { error: t('select_workspace_step.invalid_url') } });
		},
	});

	const discoveryQuery = useDoDiscovery<{
		email: string;
		organisations?: OrganisationWithWorkspaces[];
		workspaces: GroupedWorkspace[];
	}>(
		{ organisationId: activeOrganisationId },
		{
			cacheTime: 0,
			retry: false,
			enabled: false,
			select: (data) => ({
				email: data.email,
				organisations: data.organisations,
				workspaces: generateLoginStrategiesForWorkspaces(resolveDiscoveryWorkspaces(data)),
			}),
			onSuccess: (data) => {
				if ((data.organisations?.length ?? 0) > 1) {
					// Carry an incoming error (e.g. a failed SSO login) along - the redirect would drop it.
					navigateTo(Step.SELECT_ORGANISATION_FOR_LOGIN, {
						replace: true,
						state: { organisations: data.organisations, email: data.email, error: locationState?.error },
					});
					setIsLoading(false);
					return;
				}

				if (data.workspaces.length === 1) {
					// If there is only 1 workspace we don't want to automatically select it
					// if there's an error (f.e during SSO login), otherwise he can get in an endless loop
					if (!locationState?.error) {
						selectWorkspace(data.workspaces[0].url);
					}
				}
				setIsLoading(false);
			},
			onError: (error) => {
				if (
					activeOrganisationId &&
					!hasRetriedUnscopedRef.current &&
					error instanceof HttpStatusError &&
					error.status === 404
				) {
					hasRetriedUnscopedRef.current = true;
					setActiveOrganisationId(undefined);
					// The server just told us this org id is no longer valid for this email - clear
					// the session copy too, not just local state, so PasswordStep/
					// SelectAuthenticationStrategyStep (which read it independently) don't keep
					// scoping their own discovery calls to the now-invalid org.
					clearOrganisationInSession();
					return;
				}

				if (!locationState?.error) {
					navigateTo(Step.SELECT_ORGANISATION);
				} else {
					// If SSO was initiated from email step and there was an error,
					// we want to show this error on the email step
					navigateTo(Step.ENTER_EMAIL, { replace: true, state: locationState });
				}
			},
		}
	);

	// Does not clear the `organisation` session value: SelectAuthenticationStrategyStep and
	// PasswordStep (workspaceUrl's destination) scope their own discovery call with it too, for a
	// multi-org email. It's cleared once post-authentication instead (handlePostAuthentication),
	// once nothing downstream needs it any more.
	const selectWorkspace = (workspaceUrl: string) => {
		return navigateTo(workspaceUrl);
	};

	const doTokenExchange = useDoTokenExchange({
		onSuccess: () => {
			return discoveryQuery.refetch();
		},
		onError: () => {
			clearSessionOnTokenExchangeFailure.mutate();
		},
	});

	useEffect(() => {
		if (token) {
			doTokenExchange.mutate({ token });
		} else {
			void discoveryQuery.refetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token]);

	useEffect(() => {
		// Only fires from the retry-once-unscoped branch above, never on mount.
		if (hasRetriedUnscopedRef.current) {
			void discoveryQuery.refetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeOrganisationId]);

	return {
		email: discoveryQuery.data?.email,
		isLoading: isLoading,
		workspaces: discoveryQuery.data?.workspaces || [],
		selectWorkspace,
	};
};

export const useClearSession = (): UseBaseMutationResult<void, unknown, void, unknown> => {
	const navigateTo = useNavigate();

	return useDoClearSession({
		onSettled: () => {
			navigateTo(Step.ENTER_EMAIL, { replace: true });
		},
	});
};
